import {
  getCreateReferenceId,
  getPeriodDayWithPausePeriod,
  type Period,
  type PeriodComplete,
} from '@quromedical/fhir-common';
import type { AdmissionGetResponseWithVitals } from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { Col } from 'components/base';
import {
  admissionClassDisplay,
  admissionServiceDisplay,
  admissionStatusDisplay,
  getDisplay,
  getDisplayDate,
  getPeriodDayDisplay,
  getQuantityDisplay,
} from 'core/display';
import { FileCard, FormCard, Grid, type RowConfig, Text } from 'design-system';
import { usePatient } from 'hooks/usePatient';
import type React from 'react';
import { strings } from 'strings';

import { statusColor } from './internal';

interface AdmissionCardProps {
  patientId: string;
  data?: AdmissionGetResponseWithVitals;
  canEdit?: boolean;
  canViewMedia?: boolean;
  onEditPress?: () => void;
  downloadFile: DownloadFile;
  onSecondarySubmit?: () => void;
  onTertiarySubmit?: (admissionId: string) => void;
  secondaryButtonText?: string;
  tertiaryButtonText?: string;
  showSecondarySubmitButton?: boolean;
}

type DownloadFile = (mediaId: string) => Promise<void>;

const periodIsComplete = (period?: Period): period is PeriodComplete => {
  if (!period) {
    return false;
  }

  const periodEnd = (period as PeriodComplete).end;

  return !!periodEnd;
};

const createRows = (
  downloadAttachment: DownloadFile,
  admission: Partial<AdmissionGetResponseWithVitals> = {},
  canViewMedia = false,
  gender?: string,
): RowConfig[] => {
  const {
    class: cls,
    primaryIcd10Code,
    icd10Codes = [],
    chronic = [],
    reasonOfAdmission = [],
    acute = [],
    medicalAidAuthorization,
    oxygen,
    period,
    service,
    status,
    media = [],
    statusHistory = [],
    pregnancy,
    location,
  } = admission;
  const isFemale = gender === 'female';
  const showLactation = isFemale && pregnancy?.lactation;
  const showPregnancy = isFemale && pregnancy?.isPregnant;
  const showPregnancyWithDate = showPregnancy && pregnancy?.edd;

  const start = period?.start;
  const end = periodIsComplete(period) ? period.end : undefined;
  const primaryIcd10CodeDisplay = primaryIcd10Code
    ? getDisplay(primaryIcd10Code, 'code: display')
    : '';
  const hasStatusHistory = statusHistory.length > 0;

  const statusHistoryFilterPaused = statusHistory.filter(
    (history) => history.status === 'onleave',
  );

  return compact([
    {
      fields: [
        {
          type: 'text',
          label: strings.AdmissionStatusLabel,
          display: admissionStatusDisplay[status || 'unknown'],
        },
      ],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.AdmissionServiceLabel,
          display: service && admissionServiceDisplay[service],
        },
        {
          type: 'text',
          label: strings.AdmissionClass,
          display: cls && admissionClassDisplay[cls],
        },
      ],
    },
    {
      fields: location
        ? [
            {
              type: 'text',
              label: strings.Location,
              display: location.display,
            },
          ]
        : [],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.AdmissionStartLabel,
          display: getDisplayDate(start),
        },
        {
          type: 'text',
          label: strings.AdmissionEndLabel,
          display: getDisplayDate(end),
        },
      ],
    },
    medicalAidAuthorization
      ? {
          fields: [
            {
              type: 'text',
              label: strings.AdmissionAuthorizationDateLabel,
              display: getDisplayDate(medicalAidAuthorization?.date),
            },
            {
              type: 'text',
              label: strings.AdmissionAuthorizationCodeLabel,
              display: medicalAidAuthorization?.code,
            },
          ],
        }
      : undefined,
    {
      fields: compact([
        {
          type: 'text',
          label: strings.FormLabelPrimaryICD10Codes,
          display: primaryIcd10CodeDisplay,
        },
        {
          type: 'list',
          label: strings.FormLabelICD10Codes,
          display: icd10Codes.map((code) => getDisplay(code, 'code: display')),
        },
      ]),
    },
    {
      fields: compact([
        {
          type: 'list',
          label: strings.DiagnosisLabelRFA,
          display: reasonOfAdmission.map((code) =>
            getDisplay(code, 'code: display'),
          ),
        },
        {
          type: 'list',
          label: strings.DiagnosisLabelChronic,
          display: chronic.map((code) => getDisplay(code, 'code: display')),
        },
      ]),
    },
    {
      fields: compact([
        {
          type: 'list',
          label: strings.DiagnosisLabelAcute,
          display: acute.map((code) => getDisplay(code, 'display | code')),
        },
      ]),
    },
    {
      fields: compact([
        oxygen
          ? {
              type: 'text',
              label: strings.RequiredOxygenFormLabel,
              display: getQuantityDisplay(oxygen),
            }
          : undefined,
      ]),
    },

    {
      fields: compact([
        showPregnancy
          ? {
              type: 'text',
              label: strings.FormCardLabelPregnant,
              display: 'Yes',
            }
          : undefined,
        showPregnancyWithDate
          ? {
              type: 'text',
              label: strings.FormLabelEDD,
              display: getDisplayDate(pregnancy.edd),
            }
          : undefined,
      ]),
    },
    {
      fields: compact([
        showLactation
          ? {
              type: 'text',
              label: strings.FormCardLabelLactation,
              display: 'Yes',
            }
          : undefined,
      ]),
    },
    {
      fields: compact([
        hasStatusHistory
          ? {
              type: 'custom',
              label: strings.AdmissionPausedLabel,
              Display: () => (
                <Col>
                  {statusHistoryFilterPaused.map((history) => {
                    return (
                      <Col key={`${history.period.start}-${history.status}`}>
                        <Text variant="body-strong">
                          Start: {getDisplayDate(history.period.start)}
                        </Text>
                        <Text variant="body-strong">
                          End: {getDisplayDate(history.period.end)}
                        </Text>
                      </Col>
                    );
                  })}
                </Col>
              ),
            }
          : undefined,
      ]),
    },
    {
      fields: compact([
        {
          type: 'custom',
          label: strings.AdmissionDayOfService,
          Display: () => {
            const hasPausedHistory = statusHistory?.some(
              (history) => history.status === 'onleave',
            );

            const dayDisplay = hasPausedHistory
              ? getPeriodDayWithPausePeriod(statusHistory, period)
              : getPeriodDayDisplay(period, 'message', status);
            return <Text variant="body-strong">{dayDisplay}</Text>;
          },
        },
      ]),
    },
    canViewMedia
      ? {
          fields: [
            {
              type: 'custom',
              Display: () => (
                <Grid sm={1} md={2} lg={3} xl={4} gap="xs">
                  {media.map((m) => (
                    <FileCard
                      key={m.id}
                      name={m.display || strings.DisplayNotFound}
                      onPress={() =>
                        downloadAttachment(getCreateReferenceId(m))
                      }
                    />
                  ))}
                </Grid>
              ),
            },
          ],
        }
      : undefined,
  ]);
};

export const AdmissionCard: React.FC<AdmissionCardProps> = ({
  patientId,
  data,
  canEdit,
  canViewMedia,
  onEditPress,
  downloadFile,
  onSecondarySubmit,
  onTertiarySubmit,
  secondaryButtonText,
  tertiaryButtonText,
  showSecondarySubmitButton,
}) => {
  const { gender } = usePatient(patientId);

  const borderColor = statusColor[data?.status || 'unknown'];

  const rows = createRows(downloadFile, data, canViewMedia, gender);

  return (
    <FormCard
      borderColor={borderColor}
      title={strings.AdmissionCardTitle}
      rows={rows}
      onButtonPress={onEditPress}
      buttonText={strings.ButtonTextEdit}
      isButtonVisible={canEdit}
      buttonIcon="edit"
      showSecondarySubmitButton={showSecondarySubmitButton}
      showTertiarySubmitButton={canEdit}
      secondaryButtonText={secondaryButtonText}
      tertiaryButtonText={tertiaryButtonText}
      onSecondarySubmit={onSecondarySubmit}
      onTertiarySubmit={() => onTertiarySubmit?.(data?.id as string)}
    />
  );
};
